import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { CREDIT_PACKS, type OfferedCreditPack } from 'fym-shared/src/music24/creditPacks';
import { TypedMemo } from '../../../common/types';
import { type Music24DisplayCurrency, useDisplayCurrency } from '../../../music24/config/useDisplayCurrency';
import { priceMessages as messages } from '../../messages/priceMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the price and the make; each part uses its own slice of it */
import styles from './Make.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import shell from '../../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PricePacksProps {
  missing: number;
  selected: OfferedCreditPack;
  onSelect: (pack: OfferedCreditPack) => void;
}

/** The price of a pack, in whole units of the currency that Checkout charges (the same number in USD and EUR). */
export function packPriceText(
  intl: ReturnType<typeof useIntl>,
  pack: OfferedCreditPack,
  currency: Music24DisplayCurrency
): string {
  return intl.formatNumber(pack.priceUsd, { style: 'currency', currency, maximumFractionDigits: 0 });
}

/**
 * "Add a credit pack" of the price step (design f2): the packs of `CREDIT_PACKS` (the products of
 * the credit packs modal), each with "Not enough for this video" or "Enough · 40 left after it".
 */
const PricePacksComponent: React.FC<PricePacksProps> = ({ missing, selected, onSelect }) => {
  const intl = useIntl();
  const currency = useDisplayCurrency();
  return (
    <>
      <span className={shell.eyebrow}>{intl.formatMessage(messages.addPack)}</span>
      <div className={styles.packs} role="radiogroup" aria-label={intl.formatMessage(messages.addPack)}>
        {CREDIT_PACKS.map((pack) => {
          const isEnough = pack.credits >= missing;
          return (
            <button
              key={pack.id}
              type="button"
              role="radio"
              aria-checked={pack.id === selected.id}
              className={clsx(styles.pack, pack.id === selected.id && styles.packOn, !isEnough && styles.packShort)}
              onClick={() => onSelect(pack)}
            >
              <span className={styles.packCount}>{pack.credits}</span>
              <span className={styles.packPrice}>{packPriceText(intl, pack, currency)}</span>
              <span className={clsx(styles.packNote, isEnough && styles.packEnough)}>
                {isEnough
                  ? intl.formatMessage(messages.packEnough, { left: pack.credits - missing })
                  : intl.formatMessage(messages.packNotEnough)}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
};

export const PricePacks = TypedMemo(PricePacksComponent);
